/**
 * groundController - the grounded character controller for the player camera.
 *
 * The player UniversalCamera runs with applyGravity = false (see src/camera.js);
 * this owns the vertical axis instead. Once per camera update it casts one
 * downward probe ray, and hands the result to the pure resolveGroundState() in
 * groundedMotion.js: snap to the floor while grounded, integrate delta-scaled
 * gravity while not. That is what makes ramps and stairs climbable - Babylon's
 * own gravity is a raw per-frame displacement that survives the collide-and-slide
 * projection on a slope and drags you downhill ~55x faster than you can walk up.
 *
 * The horizontal half is unchanged: movement.js still writes camera.cameraDirection
 * and Babylon's ellipsoid collide-and-slide still resolves it, so walls behave
 * exactly as before. With gravity off, pushing into a ramp wedge makes that same
 * slide climb it on its own; this controller supplies the other half (contact,
 * descent, falling).
 *
 * TICK POINT: camera.onAfterCheckInputsObservable, NOT scene.onBeforeRender.
 * Scene.render() calls camera.update() BEFORE notifying onBeforeRenderObservable,
 * so a position write there would be a frame stale - and in third person the
 * player camera is not the active camera at all, so Scene.render() never updates
 * it (ChaseCameraRig._tick() calls playerCamera.update() by hand). The camera's
 * own observable fires after _updatePosition() (i.e. after collide-and-slide) and
 * before getViewMatrix(), once per update, in both views.
 */

import { Ray, Vector3 } from '@babylonjs/core';
import { createErrorScope, ErrorCategory } from '../utils/sceneErrorHandler';
import { GROUND_DEFAULTS, RESYNC_OVERRIDES, resolveGroundState } from './groundedMotion.js';

const errorHandler = createErrorScope('GroundController', ErrorCategory.ENTITY);

/** @type {WeakMap<object, GroundController>} */
const controllerRegistry = new WeakMap();

/**
 * The scene's ground controller, if one was attached (tests and solo harnesses
 * may have none). Mirrors getChaseCameraRig().
 * @param {import('@babylonjs/core').Scene | null | undefined} scene
 * @returns {GroundController | null}
 */
export function getGroundController(scene) {
  return (scene && controllerRegistry.get(scene)) || null;
}

/**
 * Attach the controller to a scene's player camera. Self-disposes when the
 * scene does, the same shape as attachClickToWalk().
 * @param {import('@babylonjs/core').Scene} scene
 * @param {import('@babylonjs/core').UniversalCamera} camera
 * @param {Object} [options]
 * @param {() => number} [options.getDeltaSeconds] frame delta override (the
 *   NullEngine reports 0 without a render loop, so tests supply their own)
 * @returns {GroundController}
 */
export function attachGroundController(scene, camera, options = {}) {
  return new GroundController(scene, camera, options);
}

export class GroundController {
  /**
   * @param {import('@babylonjs/core').Scene} scene
   * @param {import('@babylonjs/core').UniversalCamera} camera
   * @param {{getDeltaSeconds?: () => number}} [options]
   */
  constructor(scene, camera, options = {}) {
    this.scene = scene;
    this.camera = camera;
    this._disposed = false;
    /** @type {Set<string>} */
    this._suspendReasons = new Set();
    this._getDeltaSeconds =
      options.getDeltaSeconds || (() => scene.getEngine().getDeltaTime() / 1000);

    this._vy = 0;
    /** @type {number | null} */
    this._lastX = null;
    /** @type {number | null} */
    this._lastZ = null;
    /** Center height as this controller last left it - anything else moved it. */
    this._lastCenterY = null;
    /** @type {number | null} last tick's ellipsoid rest offset */
    this._lastRest = null;
    // The first tick after attach grounds the camera at its spawn height.
    this._pendingResync = true;
    /** @type {{grounded: boolean, phase: string, surfaceY: number | null, slopeDeg: number | null}} */
    this._state = { grounded: false, phase: 'void', surfaceY: null, slopeDeg: null };

    // Scratch objects reused every tick - no per-frame allocation.
    this._ray = new Ray(new Vector3(), new Vector3(0, -1, 0), GROUND_DEFAULTS.probeLength);

    this._tickObserver = camera.onAfterCheckInputsObservable.add(() => this._tick());
    this._disposeObserver = scene.onDisposeObservable.add(() => this.dispose());
    controllerRegistry.set(scene, this);
  }

  /** @returns {boolean} */
  isSuspended() {
    return this._suspendReasons.size > 0;
  }

  /**
   * Hand the vertical axis to another system for its lifetime (fly mode, the
   * ?edit editor, the arrival cinematic, drone view, the screenshot harness).
   * Reentrant per reason, matching ChaseCameraRig.suspend().
   * @param {string} reason
   */
  suspend(reason) {
    if (this._disposed) return;
    this._suspendReasons.add(reason);
    this._vy = 0;
  }

  /**
   * Release a suspend reason. Once the last one clears the controller
   * re-grounds from wherever the other system left the camera.
   * @param {string} reason
   */
  resume(reason) {
    if (this._disposed) return;
    this._suspendReasons.delete(reason);
    if (!this._suspendReasons.size) this.resync();
  }

  /**
   * Re-ground after a teleport (spawn, respawn, presenter gather, drone exit).
   * The next tick probes the whole level and snaps without the incremental
   * limits, and does not treat the jump as horizontal travel.
   */
  resync() {
    if (this._disposed) return;
    this._pendingResync = true;
    this._vy = 0;
    this._lastX = null;
    this._lastZ = null;
    this._lastCenterY = null;
    this._lastRest = null;
  }

  /**
   * Machine-readable state for end-to-end verification (window.__groundSnapshot).
   * @returns {{grounded: boolean, phase: string, vy: number, surfaceY: number | null,
   *   slopeDeg: number | null, suspended: boolean}}
   */
  snapshot() {
    return {
      grounded: this._state.grounded,
      phase: this._state.phase,
      vy: this._vy,
      surfaceY: this._state.surfaceY,
      slopeDeg: this._state.slopeDeg,
      suspended: this.isSuspended(),
    };
  }

  _tick() {
    if (this._disposed || this._suspendReasons.size) return;
    // Fly mode and the editor turn collisions off entirely; the camera is
    // noclip then and must not be pulled to the floor.
    if (!this.camera.checkCollisions) return;
    try {
      this._step();
    } catch (e) {
      errorHandler.warn('tick', `ground update failed: ${e?.message}`);
    }
  }

  _step() {
    const camera = this.camera;
    const pos = camera.position;
    const resync = this._pendingResync;
    this._pendingResync = false;

    // Distance covered by THIS frame's collide-and-slide: the tick runs
    // immediately after _updatePosition(), so the delta against the previous
    // tick is the real horizontal step, never a stale one. A teleport reports
    // zero rather than an enormous jump that would saturate the snap-down band.
    const horizontalStep =
      resync || this._lastX === null || this._lastZ === null
        ? 0
        : Math.hypot(pos.x - this._lastX, pos.z - this._lastZ);

    // Babylon's collision origin: position - ellipsoid.y + ellipsoidOffset.y.
    const eyeToCenter = camera.ellipsoid.y - camera.ellipsoidOffset.y;
    const centerY = pos.y - eyeToCenter;
    const radii = { xz: Math.max(camera.ellipsoid.x, camera.ellipsoid.z), y: camera.ellipsoid.y };

    // Everything that moved us vertically since the last tick came from
    // collide-and-slide: nothing else writes camera.position.y without going
    // through resync(), which clears this baseline.
    const climb = resync || this._lastCenterY === null ? 0 : centerY - this._lastCenterY;

    const probe = this._probe(pos.x, centerY, pos.z, resync);
    const state = resolveGroundState(
      probe,
      {
        centerY,
        vy: this._vy,
        dt: this._getDeltaSeconds(),
        horizontalStep,
        climb,
        previousRest: resync ? undefined : (this._lastRest ?? undefined),
      },
      radii,
      resync ? RESYNC_OVERRIDES : undefined,
    );

    pos.y = state.centerY + eyeToCenter;
    this._vy = state.vy;
    this._lastX = pos.x;
    this._lastZ = pos.z;
    this._lastCenterY = state.centerY;
    this._lastRest = state.rest;
    this._state = {
      grounded: state.grounded,
      phase: state.phase,
      surfaceY: probe && probe.hit ? probe.surfaceY : null,
      slopeDeg:
        probe && probe.hit
          ? (Math.acos(Math.min(1, Math.abs(probe.normalY))) * 180) / Math.PI
          : null,
    };
  }

  /**
   * Cast the downward floor probe.
   * @param {number} x
   * @param {number} centerY ellipsoid-center height
   * @param {number} z
   * @param {boolean} resync probe the whole level rather than the local band
   * @returns {import('./groundedMotion.js').GroundProbe | null}
   */
  _probe(x, centerY, z, resync) {
    this._ray.origin.copyFromFloats(x, centerY + GROUND_DEFAULTS.probeUp, z);
    this._ray.direction.copyFromFloats(0, -1, 0);
    this._ray.length =
      GROUND_DEFAULTS.probeUp +
      (resync ? GROUND_DEFAULTS.resyncProbeLength : GROUND_DEFAULTS.probeLength);

    // Collision-based predicate, deliberately mirroring the collider's own
    // mesh filter (DefaultCollisionCoordinator._collideWithWorld) so the probe
    // sees exactly what holds the player up - including invisible walls and the
    // hidden stairs wedge, which are isPickable = false. A custom predicate
    // replaces Babylon's isEnabled/isVisible/isPickable checks entirely.
    const pick = this.scene.pickWithRay(this._ray, (m) => m.checkCollisions && m.isEnabled());
    if (!pick || !pick.hit || !pick.pickedPoint) return null;

    // useVerticesNormals MUST be false: LayoutObjects.buildWedge shares
    // vertices between the slope, bottom and side faces, so the averaged
    // vertex normal on a ramp is not the slope's normal.
    //
    // Babylon documents that the returned normal ALWAYS points back towards
    // the picking ray, so a downward probe can never read a negative y - the
    // abs() is belt-and-braces, not a winding fix. The consequence worth
    // knowing: the normal cannot distinguish a floor from a ceiling, so a
    // camera embedded inside solid geometry reads that solid's underside as
    // ground. Only degenerate spawns put it there, and the collider pushes it
    // out on the first step.
    const normal = pick.getNormal(true, false);
    if (!normal) return null;
    const normalY = Math.abs(normal.y);
    const normalXZ = Math.hypot(normal.x, normal.z);

    return { hit: true, surfaceY: pick.pickedPoint.y, normalY, normalXZ };
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.camera.onAfterCheckInputsObservable.remove(this._tickObserver);
    this.scene.onDisposeObservable.remove(this._disposeObserver);
    controllerRegistry.delete(this.scene);
  }
}
